import React, {
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
  useEffect,
} from "react";
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { getAuth } from "firebase/auth";
import {
  addDoc,
  arrayUnion,
  collection,
  getDoc,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebaseConfig";
import { requestMicPermission } from "../utils/requestMicPermission";
import { setSpeakerRoute, resetSpeakerRoute, setAudioRoute, getBluetoothAvailable } from "../plugins/audioRouter";
import { syncProximityScreen, resetProximityScreen } from "../plugins/proximityScreen";
import NativeWebRTC from "../utils/nativeWebRTC";
import { postMissedCallNotification } from "../utils/missedCallNotifications";
import {
  reportOutgoingCall as reportCallKitOutgoing,
  reportOutgoingCallConnected,
  addCallKitListener,
  reportCallEnded,
  endCall as endCallKit,
  setCallKitSpeakerRoute,
  getPlatform,
  formatCallerName,
  registerForVoIPPush,
  addVoIPTokenListener,
  initializeCallNotifications,
  getPendingAccept,
  clearPendingAccept,
  hasActiveCall,
} from "../utils/callKitBridge";

const CallSessionContext = createContext(null);
export { CallSessionContext };

export function useCallSession() {
  return useContext(CallSessionContext);
}

export function CallSessionProvider({ currentUser, children }) {
  // Phase-2 invariant:
  // CallSessionContext is the sole owner of WebRTC setup,
  // call lifecycle, and audio state. UI components (DirectChat,
  // GlobalCallShell, CallPage, overlays) are strictly consumers.

  // WebRTC-related refs (mirrored from DirectChat)
  const pcRef = useRef(null);
  const answerPcRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const iceUnsubRef = useRef(null);
  const calleeCandidatesUnsubRef = useRef(null);
  const callerCandidatesUnsubRef = useRef(null);
  const calleeCandidatesProcessedRef = useRef(new Set());
  const callerCandidatesProcessedRef = useRef(new Set());
  const pendingCalleeCandidatesRef = useRef([]);
  const callTimeoutRef = useRef(null);
  const callStartTimeRef = useRef(null);
  const callPartnerIdRef = useRef(null);
  const hasLoggedIceServersRef = useRef(false);
  const speakerTouchedRef = useRef(false);
  const cleanupRanRef = useRef(false);
  const routingInitializedRef = useRef(false);
  const incomingStatusUnsubRef = useRef(null);
  const incomingEndedLoggedRef = useRef(false);
  const missedCallNotifiedRef = useRef(new Set());
  const nativeCandidateUnsubRef = useRef(null);
  const nativeConnectionUnsubRef = useRef(null);
  const nativeRemoteDescriptionReadyRef = useRef(false);

  // Call state (mirrors DirectChat)
  const [outgoingCallId, setOutgoingCallId] = useState(null);
  const [outgoingCallTarget, setOutgoingCallTarget] = useState(null);
  const [isCalling, setIsCalling] = useState(false);
  const [inCall, setInCall] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(false);
  const [incomingCall, setIncomingCall] = useState(null);
  const [incomingCallId, setIncomingCallId] = useState(null);
  const [callState, setCallState] = useState("idle");
  const [callDuration, setCallDuration] = useState(0);
  const [activePeer, setActivePeer] = useState(null);
  const [appVisibility, setAppVisibility] = useState("foreground");
  const [callProgressLabel, setCallProgressLabel] = useState("");
  const [callStatus, setCallStatus] = useState("");
  const [audioRoute, setAudioRouteState] = useState("earpiece");
  const [bluetoothAvailable, setBluetoothAvailable] = useState(false);
  const callStateRef = useRef("idle");
  const inCallRef = useRef(false);
  const acceptedHandledRef = useRef(new Set());
  const callKitAudioActiveRef = useRef(false);
  const pendingCallKitAcceptRef = useRef(null);
  const pendingCallKitAudioResolveRef = useRef(null);
  const incomingCallKitIdRef = useRef(null);
  const outgoingCallKitIdRef = useRef(null);
  const acceptInProgressRef = useRef(false);
  const acceptedCallIdRef = useRef(null);
  const terminalCallIdsRef = useRef(new Set());
  const cleanupDoneRef = useRef(new Set());
  const callKitEndedCallIdsRef = useRef(new Set());
  const coldStartRecoveryAttemptedRef = useRef(false);
  const coldStartAutoAcceptTimerRef = useRef(null);
  const coldStartOfferTimeoutRef = useRef(null);
  const coldStartCallRef = useRef(null);
  const incomingCallIdRef = useRef(null);
  const outgoingCallIdRef = useRef(null);
  const incomingCallRef = useRef(null);
  const callKitHydrationUnsubRef = useRef(null);
  const ringingWrittenRef = useRef(new Set());
  const connectedWrittenRef = useRef(new Set());
  const callStatusRef = useRef("");
  const ringingTimeoutRef = useRef(null);
  const noAnswerTimeoutRef = useRef(null);
  const ringbackTimerRef = useRef(null);
  const ringbackContextRef = useRef(null);
  const ringbackPlayingRef = useRef(false);
  const terminationReasonRef = useRef(null);
  // Holds latest action functions so persistent listeners avoid stale closures
  const actionsRef = useRef({ acceptCall: null, declineCall: null, endCall: null });

  const effectiveUser = useMemo(() => {
    const authUser = getAuth().currentUser || null;

    if (currentUser?.uid) {
      return currentUser;
    }

    if (authUser?.uid) {
      return { uid: authUser.uid };
    }

    return null;
  }, [currentUser]);

  const useNativeWebRTC = getPlatform() === "ios";

  // 📞 CallKit Event Listeners (iOS only)
  // Registered ONCE on mount. All state accessed via refs to avoid stale closures.
  // Cleaned up only on full component unmount.
  useEffect(() => {
    if (getPlatform() !== 'ios') return;

    console.log('📲 Setting up persistent CallKit event listeners...');

    // User tapped "Accept" in CallKit UI
    const cleanupAccept = addCallKitListener('callAccepted', (event) => {
      console.log('✅ CallKit: User accepted call', event);
      const { callId, callKitId } = event;

      if (callKitId) {
        incomingCallKitIdRef.current = callKitId;
      }

      pendingCallKitAcceptRef.current = callId || null;

      startAcceptFromCallKit(callId);
    });

    // User tapped "Decline" or "End Call" in CallKit UI
    const cleanupEnd = addCallKitListener('callEnded', (event) => {
      console.log('🔴 CallKit: User ended call', event);
      const { callId, callKitId } = event;

      if (callKitId) {
        incomingCallKitIdRef.current = callKitId;
      }

      const currentOutgoing = outgoingCallIdRef.current;
      const currentIncoming = incomingCallIdRef.current;
      const resolvedCallId =
        currentIncoming || currentOutgoing || callId || null;
      const isIncomingRinging =
        currentIncoming &&
        callStateRef.current === "ringing_incoming" &&
        !inCallRef.current;
      // If we have a call id and we're not in an active call, treat as decline.
      if (resolvedCallId && !inCallRef.current) {
        console.log('📞 Ending incoming call from CallKit UI');
        actionsRef.current.declineCall?.(resolvedCallId);
        return;
      }
      // Fall back to local state when callId is missing.
      if (isIncomingRinging) {
        console.log('📞 Ending call from CallKit UI');
        actionsRef.current.declineCall?.(resolvedCallId);
        return;
      }
      if (currentOutgoing || currentIncoming) {
        console.log('dY"z Ending call from CallKit UI');
        actionsRef.current.endCall?.(resolvedCallId);
      }
    });

    const cleanupAudio = addCallKitListener('audioActivated', () => {
      console.log('🔊 [JS] audioActivated received');
      callKitAudioActiveRef.current = true;
      if (pendingCallKitAudioResolveRef.current) {
        pendingCallKitAudioResolveRef.current();
        pendingCallKitAudioResolveRef.current = null;
      }
    });

    // Cleanup ONLY on full component unmount
    return () => {
      console.log('👋 Cleaning up CallKit listeners (unmount)');
      cleanupAccept();
      cleanupEnd();
      cleanupAudio();
    };
  }, []); // Empty deps — persistent for entire component lifetime

  const awaitCallKitAudioActive = async () => {
    if (getPlatform() !== "ios") return;
    if (callKitAudioActiveRef.current) return;
    console.log('⏳ [JS] Waiting for CallKit didActivate...');
    await new Promise((resolve) => {
      pendingCallKitAudioResolveRef.current = resolve;
      // 5s timeout — background wake can take several seconds
      setTimeout(() => {
        console.warn('⚠️ [JS] CallKit audio wait timed out after 5s');
        resolve();
      }, 5000);
    });
  };

  const cleanupCallKitHydration = () => {
    if (callKitHydrationUnsubRef.current) {
      callKitHydrationUnsubRef.current();
      callKitHydrationUnsubRef.current = null;
    }
  };

  const clearColdStartTimers = () => {
    if (coldStartAutoAcceptTimerRef.current) {
      clearTimeout(coldStartAutoAcceptTimerRef.current);
      coldStartAutoAcceptTimerRef.current = null;
    }
    if (coldStartOfferTimeoutRef.current) {
      clearTimeout(coldStartOfferTimeoutRef.current);
      coldStartOfferTimeoutRef.current = null;
    }
  };

  const clearPendingAcceptEverywhere = async () => {
    try {
      await clearPendingAccept();
    } catch (e) {
      // ignore
    }
  };

  const logColdStart = async (message) => {
    const entry = `${new Date().toISOString()} ${message}`;
    console.log(entry);
    try {
      const existing = await Preferences.get({ key: "coldStartLog" });
      const items = existing?.value ? JSON.parse(existing.value) : [];
      const next = Array.isArray(items) ? items.concat(entry).slice(-50) : [entry];
      await Preferences.set({ key: "coldStartLog", value: JSON.stringify(next) });
    } catch (e) {
      // ignore
    }
  };

  const startAcceptFromCallKit = async (callId) => {
    if (!callId) return;
    if (acceptInProgressRef.current) return;
    if (
      inCallRef.current ||
      callStateRef.current === "connecting" ||
      callStateRef.current === "connected"
    ) {
      return;
    }
    if (acceptedCallIdRef.current === callId) {
      return;
    }

    const currentIncomingId = incomingCallIdRef.current;
    const currentIncoming = incomingCallRef.current;
    if (currentIncomingId === callId && currentIncoming?.offer) {
      pendingCallKitAcceptRef.current = null;
      performAccept(callId, currentIncoming);
      return;
    }

    cleanupCallKitHydration();

    const callRef = doc(db, "calls", callId);
    try {
      const snap = await getDoc(callRef);
      const data = snap.data();
      if (!data) {
        await endCallKitIfNeeded(callId, incomingCallKitIdRef.current || callId, "remote");
        clearPendingAcceptEverywhere();
        return;
      }
      if (isTerminalStatus(data.status) || isDeclinedStatus(data)) {
        await endCallKitIfNeeded(callId, incomingCallKitIdRef.current || callId, "remote");
        clearPendingAcceptEverywhere();
        return;
      }
      if (data?.offer) {
        pendingCallKitAcceptRef.current = null;
        const callDoc = { id: callId, ...data };
        if (callStateRef.current !== "ringing_incoming") {
          setIncomingCallData(callDoc);
        }
        performAccept(callId, callDoc);
        return;
      }
    } catch (err) {
      console.error("[CallEngine] CallKit hydration getDoc failed:", err);
    }

    const timeoutId = setTimeout(() => {
      cleanupCallKitHydration();
    }, 8000);

    const unsub = onSnapshot(callRef, (snap) => {
      const data = snap.data();
      if (!data) return;

      if (data.status === "ended" || isDeclinedStatus(data)) {
        endCallKitIfNeeded(callId, incomingCallKitIdRef.current || callId, "remote");
        cleanupCallKitHydration();
        return;
      }
      if (data.offer) {
        cleanupCallKitHydration();
        pendingCallKitAcceptRef.current = null;
        const callDoc = { id: callId, ...data };
        if (callStateRef.current !== "ringing_incoming") {
          setIncomingCallData(callDoc);
        }
        performAccept(callId, callDoc);
      }
    }, (error) => {
      console.warn("[CallEngine] CallKit hydration listener error:", error);
      endCallKitIfNeeded(callId, incomingCallKitIdRef.current || callId, "remote").catch(() => {});
      cleanupCallKitHydration();
      clearPendingAcceptEverywhere();
    });

    callKitHydrationUnsubRef.current = () => {
      clearTimeout(timeoutId);
      unsub();
    };
  };

  const scheduleColdStartFallback = (callDoc) => {
    clearColdStartTimers();
    coldStartAutoAcceptTimerRef.current = setTimeout(() => {
      if (callStateRef.current === "connected" || inCallRef.current || !!activePeer) {
        clearColdStartTimers();
        return;
      }
      if (callStateRef.current === "idle") {
        clearColdStartTimers();
        return;
      }
      logColdStart("ColdStart: auto-accept timed out").finally(async () => {
        await forceResetToIdle();
        if (!coldStartCallRef.current) return;
        try {
          const snap = await getDoc(coldStartCallRef.current);
          const data = snap.data();
        if (data?.status === "ringing") {
          await logColdStart("ColdStart: fallback triggered");
          setIncomingCallData({ id: callDoc.id, ...data });
        }
        } catch (e) {
          // ignore
        } finally {
          clearColdStartTimers();
        }
      });
    }, 4000);
  };

  const attemptColdStartAccept = async (callId, callKitId, callDoc) => {
    if (acceptInProgressRef.current) return;
    if (callStateRef.current !== "idle" && callStateRef.current !== "ringing_incoming") {
      await logColdStart(
        `ColdStart: attempt blocked (callState=${callStateRef.current}, inCall=${inCallRef.current}, hasPeer=${!!activePeer})`
      );
      if (
        callStateRef.current === "connecting" &&
        !inCallRef.current &&
        !activePeer
      ) {
        await forceResetToIdle();
      } else {
        return;
      }
    }
    const isActive = await hasActiveCall(callKitId);
    if (!isActive) {
      await logColdStart("ColdStart: CallKit active? false");
      await clearPendingAcceptEverywhere();
      return;
    }
    await logColdStart("ColdStart: CallKit active? true");
    await logColdStart("ColdStart: auto-accept started");
    performAccept(callId, callDoc);
    scheduleColdStartFallback(callDoc);
  };



  // 📝 Token Registration (VoIP & FCM)
  // Register proper token based on platform so backend knows how to notify
  useEffect(() => {
    const registerToken = async () => {
      if (!effectiveUser?.uid) return;

      const platform = getPlatform();
      console.log(`👤 Initializing CallKit/VoIP for user ${effectiveUser.uid} on ${platform}`);

      // 🚀 Initialize native permissions & tokens
      // On Android, this requests "Full Screen Intent" permissions if missing
      const result = await initializeCallNotifications();
      console.log('Call initialization result:', result);

      // iOS: Handle VoIP Token
      if (platform === 'ios') {
        if (result.token) {
          updateVoIPToken(effectiveUser.uid, result.token);
        }

        // Listen for token refresh
        const cleanup = addVoIPTokenListener((data) => {
          if (data.token) {
            updateVoIPToken(effectiveUser.uid, data.token);
          }
        });

        return cleanup;
      }

      // Android: Update platform flag
      if (platform === 'android') {
        const userRef = doc(db, 'users', effectiveUser.uid);
        try {
          await updateDoc(userRef, {
            platform: 'android',
            lastTokenUpdate: serverTimestamp()
          });
          console.log('✅ Android platform flag updated in Firestore');
        } catch (err) {
          console.error('❌ Failed to update Android platform flag:', err);
        }
      }
    };

    const cleanupPromise = registerToken();
    return () => {
      cleanupPromise.then(cleanup => cleanup && cleanup());
    };
  }, [effectiveUser?.uid]);

  // Cold-start recovery after auth (iOS only)
  useEffect(() => {
    const runRecovery = async () => {
      if (getPlatform() !== "ios") return;
      if (!effectiveUser?.uid) return;
      if (coldStartRecoveryAttemptedRef.current) return;
      if (callStateRef.current !== "idle") return;
      coldStartRecoveryAttemptedRef.current = true;

      const pending = await getPendingAccept();
      const callId = pending?.callId;
      const callKitId = pending?.callKitId;
      const timestamp = pending?.timestamp || 0;
      if (!callId || !callKitId) return;

      await logColdStart("ColdStart: pending detected");
      const ageMs = Date.now() - timestamp * 1000;
      if (ageMs > 60000) {
        await endCallKitIfNeeded(callId, callKitId, "unanswered");
        await clearPendingAcceptEverywhere();
        return;
      }

      await logColdStart("ColdStart: JS detected pending");
      const callRef = doc(db, "calls", callId);
      coldStartCallRef.current = callRef;
      try {
        const snap = await getDoc(callRef);
        const data = snap.data();
        await logColdStart("ColdStart: callDoc fetched");
        if (!data) {
          await endCallKitIfNeeded(callId, callKitId, "remote");
          await clearPendingAcceptEverywhere();
          return;
        }
        if (data.status === "ended" || isDeclinedStatus(data)) {
          await endCallKitIfNeeded(callId, callKitId, "remote");
          await clearPendingAcceptEverywhere();
          return;
        }
        if (data.offer) {
          await logColdStart("ColdStart: offer found? true");
          const callDoc = { id: callId, ...data };
          await logColdStart("ColdStart: attempt scheduled");
          setTimeout(() => {
            attemptColdStartAccept(callId, callKitId, callDoc);
          }, 150);
          return;
        }
        await logColdStart("ColdStart: offer found? false");
      } catch (err) {
        console.error("ColdStart: getDoc failed:", err);
      }

      const timeoutId = setTimeout(async () => {
        clearColdStartTimers();
        const snap = await getDoc(callRef);
        const data = snap.data();
        if (!data) {
          await endCallKitIfNeeded(callId, callKitId, "remote");
          await clearPendingAcceptEverywhere();
          return;
        }
        if (data.status === "ended" || isDeclinedStatus(data)) {
          await endCallKitIfNeeded(callId, callKitId, "remote");
          await clearPendingAcceptEverywhere();
          return;
        }
        if (data?.offer) {
          await logColdStart("ColdStart: offer found? true");
          const callDoc = { id: callId, ...data };
          await logColdStart("ColdStart: attempt scheduled");
          setTimeout(() => {
            attemptColdStartAccept(callId, callKitId, callDoc);
          }, 150);
        } else if (data) {
          console.log("ColdStart: offer wait timeout, showing modal");
          setIncomingCallData({ id: callId, ...data });
          await clearPendingAcceptEverywhere();
        }
      }, 5000);
      coldStartOfferTimeoutRef.current = timeoutId;

      const unsub = onSnapshot(callRef, (snap) => {
        const data = snap.data();
        if (!data) {
          clearColdStartTimers();
          endCallKitIfNeeded(callId, callKitId, "remote").catch(() => {});
          clearPendingAcceptEverywhere();
          return;
        }

        if (data.status === "ended" || isDeclinedStatus(data)) {
          clearColdStartTimers();
          endCallKitIfNeeded(callId, callKitId, "remote").catch(() => {});
          clearPendingAcceptEverywhere();
          return;
        }
        if (data.offer) {
          clearColdStartTimers();
          logColdStart("ColdStart: offer found? true").catch(() => {});
          const callDoc = { id: callId, ...data };
          logColdStart("ColdStart: attempt scheduled").catch(() => {});
          setTimeout(() => {
            attemptColdStartAccept(callId, callKitId, callDoc);
          }, 150);
        }
      }, (error) => {
        console.warn("[CallEngine] Cold-start listener error:", error);
        clearColdStartTimers();
        endCallKitIfNeeded(callId, callKitId, "remote").catch(() => {});
        clearPendingAcceptEverywhere();
      });

      callKitHydrationUnsubRef.current = () => {
        clearTimeout(timeoutId);
        unsub();
      };
    };

    runRecovery();
  }, [effectiveUser?.uid]);

  // Helper to update VoIP token in Firestore
  const updateVoIPToken = async (uid, token) => {
    try {
      const userRef = doc(db, 'users', uid);
      await updateDoc(userRef, {
        voipToken: token,
        platform: 'ios',
        lastTokenUpdate: serverTimestamp()
      });
      console.log('✅ VoIP token saved to Firestore');
    } catch (err) {
      console.error('❌ Failed to save VoIP token:', err);
    }
  };

  async function getIceServers() {
    try {
      const fn = httpsCallable(functions, "getIceServers");
      const result = await fn();
      if (!hasLoggedIceServersRef.current) {
        console.log("[BlueWire] ICE servers from callable:", result.data);
        hasLoggedIceServersRef.current = true;
      }
      const servers =
        result.data?.iceServers ??
        result.data?.ice_servers ??
        result.data?.token?.iceServers ??
        result.data?.token?.ice_servers ??
        [];
      console.log("[BlueWire] Parsed ICE servers:", servers);
      return servers;
    } catch (err) {
      console.error("Failed to get ICE servers:", err);
      return [];
    }
  }

  async function initializeNativeWebRTC(callId, role) {
    const iceServers = await getIceServers();
    try {
      await NativeWebRTC.initialize({ iceServers });
    } catch (err) {
      console.error("[NativeWebRTC] initialize failed:", err);
      throw err;
    }

    if (nativeCandidateUnsubRef.current) {
      nativeCandidateUnsubRef.current();
      nativeCandidateUnsubRef.current = null;
    }

    if (nativeConnectionUnsubRef.current) {
      nativeConnectionUnsubRef.current();
      nativeConnectionUnsubRef.current = null;
    }

    const candidateHandle = NativeWebRTC.addListener(
      "iceCandidate",
      async (event) => {
        if (!event?.candidate) return;
        const candidate = {
          candidate: event.candidate,
          sdpMid: event.sdpMid ?? null,
          sdpMLineIndex: event.sdpMLineIndex ?? 0,
        };
        try {
          const callRef = doc(db, "calls", callId);
          if (role === "caller") {
            await updateDoc(callRef, {
              callerCandidates: arrayUnion(candidate),
            });
          } else {
            await updateDoc(callRef, {
              calleeCandidates: arrayUnion(candidate),
            });
          }
        } catch (err) {
          console.error("[NativeWebRTC] failed to save ICE candidate:", err);
        }
      }
    );
    nativeCandidateUnsubRef.current = () => candidateHandle.remove();

    const connectionHandle = NativeWebRTC.addListener(
      "connectionState",
      (event) => {
        console.log("[NativeWebRTC] connection state:", event?.state);
        if (event?.state === "connected") {
          markConnected(callId);
        }
      }
    );
    nativeConnectionUnsubRef.current = () => connectionHandle.remove();
  }

  async function addNativeIceCandidate(candidate) {
    try {
      await NativeWebRTC.addIceCandidate({
        candidate: candidate.candidate,
        sdpMid: candidate.sdpMid,
        sdpMLineIndex: candidate.sdpMLineIndex,
      });
    } catch (err) {
      console.error("[NativeWebRTC] addIceCandidate failed:", err);
    }
  }

  async function flushPendingCalleeCandidatesNative() {
    const pending = pendingCalleeCandidatesRef.current.splice(0);
    for (const candidate of pending) {
      await addNativeIceCandidate(candidate);
    }
  }

  async function logCall(type, otherUserId, details = {}) {
    const user = effectiveUser;
    if (!user?.uid || !otherUserId) return;

    try {
      await addDoc(collection(db, "users", user.uid, "callLogs"), {
        type,
        otherUserId,
        timestamp: serverTimestamp(),
        details,
      });
    } catch (err) {
      console.error("Failed to log call:", err);
    }
  }

  function logCallWithDuration(type, otherUserId) {
    if (!otherUserId) return;

    if (callStartTimeRef.current) {
      const durationMs = Date.now() - callStartTimeRef.current;
      const seconds = Math.floor(durationMs / 1000);
      setCallDuration(seconds);
      logCall("duration", otherUserId, { seconds });
      callStartTimeRef.current = null;
    }

    logCall(type, otherUserId);
  }

  const isDeclinedStatus = (data) =>
    data?.status === "declined" ||
    (data?.status === "ended" && data?.reason === "declined");

  const isTerminalStatus = (status) =>
    ["declined", "ended", "failed", "no_answer"].includes(
      String(status || "").toLowerCase()
    );

  const markTerminalCall = (callId) => {
    if (!callId) return;
    terminalCallIdsRef.current.add(callId);
  };

  const endCallKitIfNeeded = async (callId, callKitId, reason = "remote") => {
    if (getPlatform() !== "ios") return;
    if (!callId || !callKitId) return;
    if (callKitEndedCallIdsRef.current.has(callId)) return;
    callKitEndedCallIdsRef.current.add(callId);
    try {
      await reportCallEnded(callKitId, reason);
    } catch (err) {
      console.warn("Failed to end CallKit call:", err);
    }
  };

  const cleanupCallById = async (callId, reason = "remote") => {
    if (!callId) return;
    if (cleanupDoneRef.current.has(callId)) return;
    cleanupDoneRef.current.add(callId);
    markTerminalCall(callId);
    if (acceptedCallIdRef.current === callId) {
      acceptedCallIdRef.current = null;
    }

    stopRingback();
    clearPendingAcceptEverywhere();
    clearColdStartTimers();
    cleanupCallKitHydration();

    if (incomingStatusUnsubRef.current) {
      incomingStatusUnsubRef.current();
      incomingStatusUnsubRef.current = null;
    }
    incomingEndedLoggedRef.current = false;

    if (iceUnsubRef.current) {
      iceUnsubRef.current();
      iceUnsubRef.current = null;
    }
    if (calleeCandidatesUnsubRef.current) {
      calleeCandidatesUnsubRef.current();
      calleeCandidatesUnsubRef.current = null;
    }
    if (callerCandidatesUnsubRef.current) {
      callerCandidatesUnsubRef.current();
      callerCandidatesUnsubRef.current = null;
    }
    if (nativeCandidateUnsubRef.current) {
      nativeCandidateUnsubRef.current();
      nativeCandidateUnsubRef.current = null;
    }
    if (nativeConnectionUnsubRef.current) {
      nativeConnectionUnsubRef.current();
      nativeConnectionUnsubRef.current = null;
    }

    if (getPlatform() === "ios") {
      const callKitId =
        incomingCallKitIdRef.current || outgoingCallKitIdRef.current || null;
      await endCallKitIfNeeded(callId, callKitId, reason);
    }

    clearIncomingCallState();
    cleanupWebRTC();
    cleanupCallUI();
    outgoingCallKitIdRef.current = null;
    incomingCallKitIdRef.current = null;
  };

  const waitForOffer = (callId, timeoutMs = 8000) => {
    if (!callId) return Promise.resolve(null);
    const callRef = doc(db, "calls", callId);

    return new Promise((resolve) => {
      let settled = false;
      let unsub = null;
      let timeoutId = null;

      const finish = (result) => {
        if (settled) return;
        settled = true;
        if (timeoutId) clearTimeout(timeoutId);
        if (typeof unsub === "function") unsub();
        resolve(result);
      };

      timeoutId = setTimeout(() => finish(null), timeoutMs);

      unsub = onSnapshot(
        callRef,
        (snap) => {
          const data = snap.data();
          if (!data) return;

          if (data.offer) {
            finish({ id: callId, ...data });
            return;
          }

          if (
            data.status === "ended" ||
            isDeclinedStatus(data) ||
            data.status === "failed" ||
            data.status === "no_answer"
          ) {
            finish({ id: callId, ...data });
          }
        },
        () => finish(null)
      );
    });
  };

  async function createPeerConnection(callId) {
    const iceServers = await getIceServers();

    const pc = new RTCPeerConnection({ iceServers });
    pcRef.current = pc;

    pc.onicecandidate = async (event) => {
      if (event.candidate) {
        const candidate = event.candidate.toJSON();
        const callRef = doc(db, "calls", callId);

        await updateDoc(callRef, {
          callerCandidates: arrayUnion(candidate),
        });
      }
    };

    pc.oniceconnectionstatechange = () => {
      console.log("Caller ICE Connection State:", pc.iceConnectionState);
      if (
        pc.iceConnectionState === "connected" ||
        pc.iceConnectionState === "completed"
      ) {
        markConnected(callId);
      }
    };

    pc.ontrack = (event) => {
      console.log("Caller received remote track", event.track?.id);
      if (!remoteAudioRef.current) return;

      if (!remoteStreamRef.current) {
        remoteStreamRef.current = new MediaStream();
      } else {
        remoteStreamRef.current.getTracks().forEach((track) => {
          remoteStreamRef.current.removeTrack(track);
        });
      }

      remoteStreamRef.current.addTrack(event.track);
      remoteAudioRef.current.srcObject = remoteStreamRef.current;
      remoteAudioRef.current
        .play()
        .catch((err) =>
          console.warn("Remote audio playback blocked:", err)
        );
    };

    return pc;
  }

  async function createPeerConnectionForAnswer(callId) {
    const iceServers = await getIceServers();
    const pc = new RTCPeerConnection({ iceServers });
    answerPcRef.current = pc;

    pc.onicecandidate = async (event) => {
      if (event.candidate) {
        const candidate = event.candidate.toJSON();
        await updateDoc(doc(db, "calls", callId), {
          calleeCandidates: arrayUnion(candidate),
        });
      }
    };

    pc.oniceconnectionstatechange = () => {
      console.log("Callee ICE Connection State:", pc.iceConnectionState);
      if (
        pc.iceConnectionState === "connected" ||
        pc.iceConnectionState === "completed"
      ) {
        markConnected(callId);
      }
    };

    pc.ontrack = (event) => {
      console.log("Callee received remote track", event.track?.id);
      if (!remoteAudioRef.current) return;

      if (!remoteStreamRef.current) {
        remoteStreamRef.current = new MediaStream();
      } else {
        remoteStreamRef.current.getTracks().forEach((track) => {
          remoteStreamRef.current.removeTrack(track);
        });
      }

      remoteStreamRef.current.addTrack(event.track);
      remoteAudioRef.current.srcObject = remoteStreamRef.current;
      remoteAudioRef.current
        .play()
        .catch((err) =>
          console.warn("Remote audio playback blocked:", err)
        );
    };
    return pc;
  }

  function listenForCallerCandidates(callId, pc) {
    const callRef = doc(db, "calls", callId);
    const processed = callerCandidatesProcessedRef.current;

    return onSnapshot(callRef, (snap) => {
      const data = snap.data();
      if (!data) return;
      if (terminalCallIdsRef.current.has(callId)) return;

      if (
        data.status === "ended" ||
        isDeclinedStatus(data) ||
        data.status === "failed" ||
        data.status === "no_answer"
      ) {
        console.log("Remote caller ended the call.");
        cleanupCallById(callId, "remote");
        return;
      }

      if (!data.callerCandidates) return;

      data.callerCandidates.forEach(async (candidate) => {
        const key = JSON.stringify(candidate);
        if (processed.has(key)) return;
        processed.add(key);
        try {
          await pc.addIceCandidate(new RTCIceCandidate(candidate));
        } catch (err) {
          console.error("Error adding caller ICE:", err);
        }
      });
    }, (error) => {
      console.warn("[CallEngine] Caller candidates listener error:", error);
      cleanupCallById(callId, "remote").catch(() => {});
    });
  }

  function listenForCallerCandidatesNative(callId) {
    const callRef = doc(db, "calls", callId);
    const processed = callerCandidatesProcessedRef.current;

    return onSnapshot(callRef, (snap) => {
      const data = snap.data();
      if (!data) return;
      if (terminalCallIdsRef.current.has(callId)) return;

      if (
        data.status === "ended" ||
        isDeclinedStatus(data) ||
        data.status === "failed" ||
        data.status === "no_answer"
      ) {
        console.log("Remote caller ended the call.");
        cleanupCallById(callId, "remote");
        return;
      }

      if (!data.callerCandidates) return;
      data.callerCandidates.forEach(async (candidate) => {
        const key = JSON.stringify(candidate);
        if (processed.has(key)) return;
        processed.add(key);
        await addNativeIceCandidate(candidate);
      });
    }, (error) => {
      console.warn("[CallEngine] Native caller candidates listener error:", error);
      cleanupCallById(callId, "remote").catch(() => {});
    });
  }

  async function getLocalAudioStream() {
    try {
      const baseConstraints = {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: 1,
      };

      const audioConstraints =
        typeof Capacitor?.getPlatform === "function" &&
          Capacitor.getPlatform() === "android"
          ? { ...baseConstraints, sampleRate: 48000 }
          : baseConstraints;

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints,
      });
      localStreamRef.current = stream;
      return stream;
    } catch (err) {
      console.error("Microphone permission denied:", err);
      throw err;
    }
  }

  function cleanupWebRTC() {
    if (useNativeWebRTC) {
      if (nativeCandidateUnsubRef.current) {
        nativeCandidateUnsubRef.current();
        nativeCandidateUnsubRef.current = null;
      }
      if (nativeConnectionUnsubRef.current) {
        nativeConnectionUnsubRef.current();
        nativeConnectionUnsubRef.current = null;
      }
      nativeRemoteDescriptionReadyRef.current = false;
      NativeWebRTC.close().catch((err) =>
        console.warn("[NativeWebRTC] close failed (ignored)", err)
      );
    }

    if (iceUnsubRef.current) {
      iceUnsubRef.current();
      iceUnsubRef.current = null;
    }

    if (callerCandidatesUnsubRef.current) {
      callerCandidatesUnsubRef.current();
      callerCandidatesUnsubRef.current = null;
    }

    if (calleeCandidatesUnsubRef.current) {
      calleeCandidatesUnsubRef.current();
      calleeCandidatesUnsubRef.current = null;
    }

    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }

    if (answerPcRef.current) {
      answerPcRef.current.close();
      answerPcRef.current = null;
    }

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => track.stop());
      localStreamRef.current = null;
    }

    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null;
    }

    if (remoteStreamRef.current) {
      remoteStreamRef.current.getTracks().forEach((track) => track.stop());
      remoteStreamRef.current = null;
    }

    calleeCandidatesProcessedRef.current.clear();
    callerCandidatesProcessedRef.current.clear();
    pendingCalleeCandidatesRef.current = [];
  }

  async function forceResetToIdle() {
    try {
      cleanupWebRTC();
      clearIncomingCallState();
      cleanupCallUI();
    } finally {
      acceptInProgressRef.current = false;
      clearColdStartTimers();
      await logColdStart("ColdStart: force reset to idle");
    }
  }

  function cleanupCallUI() {
    setIsCalling(false);
    setOutgoingCallId(null);
    setOutgoingCallTarget(null);
    setInCall(false);
    setIsMuted(false);
    setIsSpeakerOn(false);
    setAudioRouteState("earpiece");
    setCallProgressLabel("");
    setCallStatus("");
    callStatusRef.current = "";
    ringbackPlayingRef.current = false;
    if (ringbackTimerRef.current) {
      clearInterval(ringbackTimerRef.current);
      ringbackTimerRef.current = null;
    }
    if (speakerTouchedRef.current && Capacitor.isNativePlatform()) {
      try {
        resetSpeakerRoute();
      } catch (e) {
        console.warn("[AudioRouter] reset failed (ignored)", e);
      }
    }
    if (callTimeoutRef.current) {
      clearTimeout(callTimeoutRef.current);
      callTimeoutRef.current = null;
    }
    if (ringingTimeoutRef.current) {
      clearTimeout(ringingTimeoutRef.current);
      ringingTimeoutRef.current = null;
    }
    if (noAnswerTimeoutRef.current) {
      clearTimeout(noAnswerTimeoutRef.current);
      noAnswerTimeoutRef.current = null;
    }
    callPartnerIdRef.current = null;
    callStartTimeRef.current = null;
    setCallDuration(0);
    setActivePeer(null);
    setCallState("idle");
    routingInitializedRef.current = false;
  }

  function cleanupOutgoingCall() {
    const callId = outgoingCallId || incomingCallId || null;
    console.log("[CallEngine] cleanupOutgoingCall (guarded):", callId);
    if (callId && acceptedHandledRef.current.has(callId)) {
      acceptedHandledRef.current.delete(callId);
    }
    if (cleanupRanRef.current) return;
    cleanupRanRef.current = true;
    if (callId) {
      cleanupCallById(callId, "remote");
      return;
    }
    cleanupCallKitHydration();
    clearColdStartTimers();
    cleanupWebRTC();
    cleanupCallUI();
  }

  function clearIncomingCallState() {
    if (!incomingCallId && !incomingCall && callState === "idle") {
      return;
    }
    setIncomingCall(null);
    setIncomingCallId(null);
    incomingCallRef.current = null;
    incomingCallIdRef.current = null;
    setActivePeer(null);
    setCallState("idle");
    callStateRef.current = "idle";
    acceptedCallIdRef.current = null;
    incomingCallKitIdRef.current = null;
    pendingCallKitAcceptRef.current = null;
    pendingCallKitAudioResolveRef.current = null;
    cleanupCallKitHydration();
    clearColdStartTimers();
  }

  function flushPendingCalleeCandidates(pc) {
    if (!pc) return;
    const pending = pendingCalleeCandidatesRef.current.splice(0);
    pending.forEach(async (candidate) => {
      try {
        await pc.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (err) {
        console.error("Error adding queued callee ICE:", err);
      }
    });
  }

  function queueOrAddCalleeCandidate(pc, candidate) {
    if (!pc?.currentRemoteDescription) {
      pendingCalleeCandidatesRef.current.push(candidate);
      return;
    }

    pc.addIceCandidate(new RTCIceCandidate(candidate)).catch((err) => {
      console.error("Error adding callee ICE:", err);
    });
  }

  function listenForCalleeCandidates(callId, pc) {
    const callRef = doc(db, "calls", callId);
    const processed = calleeCandidatesProcessedRef.current;

    const unsub = onSnapshot(callRef, (snap) => {
      const data = snap.data();
      if (!data || !data.calleeCandidates) return;

      data.calleeCandidates.forEach((candidate) => {
        const key = JSON.stringify(candidate);
        if (processed.has(key)) return;
        processed.add(key);

        queueOrAddCalleeCandidate(pc, candidate);
      });
    }, (error) => {
      console.warn("[CallEngine] Callee candidates listener error:", error);
      cleanupCallById(callId, "remote").catch(() => {});
    });

    return unsub;
  }

  function listenForCalleeCandidatesNative(callId) {
    const callRef = doc(db, "calls", callId);
    const processed = calleeCandidatesProcessedRef.current;

    return onSnapshot(callRef, (snap) => {
      const data = snap.data();
      if (!data || !data.calleeCandidates) return;

      data.calleeCandidates.forEach((candidate) => {
        const key = JSON.stringify(candidate);
        if (processed.has(key)) return;
        processed.add(key);

        if (!nativeRemoteDescriptionReadyRef.current) {
          pendingCalleeCandidatesRef.current.push(candidate);
          return;
        }

        addNativeIceCandidate(candidate);
      });
    }, (error) => {
      console.warn("[CallEngine] Native callee candidates listener error:", error);
      cleanupCallById(callId, "remote").catch(() => {});
    });
  }

  function toggleMute() {
    if (useNativeWebRTC) {
      const newMuted = !isMuted;
      NativeWebRTC.setMuted({ muted: newMuted }).catch((err) =>
        console.warn("[NativeWebRTC] setMuted failed", err)
      );
      setIsMuted(newMuted);
      return;
    }
    if (!localStreamRef.current) return;

    const newMuted = !isMuted;
    localStreamRef.current.getAudioTracks().forEach((track) => {
      track.enabled = !newMuted;
    });
    setIsMuted(newMuted);
  }

  function toggleSpeaker() {
    const newState =
      getPlatform() === "android" ? audioRoute !== "speaker" : !isSpeakerOn;

    if (remoteAudioRef.current) {
      try {
        remoteAudioRef.current.setAttribute("playsinline", true);
        remoteAudioRef.current.setAttribute("webkit-playsinline", true);

        if (newState) {
          remoteAudioRef.current.setAttribute("x-webkit-airplay", "allow");
        } else {
          remoteAudioRef.current.removeAttribute("x-webkit-airplay");
        }
      } catch (err) {
        console.error("Speaker toggle failed:", err);
      }
    }

    speakerTouchedRef.current = true;
    if (getPlatform() === "android") {
      setAudioRouteState(newState ? "speaker" : "earpiece");
      setAudioRoute(newState ? "speaker" : "earpiece");
    } else {
      setSpeakerRoute(newState);
    }
    if (getPlatform() === "ios") {
      setCallKitSpeakerRoute(newState);
    }
    setIsSpeakerOn(newState);
  }

  async function selectAudioRoute(route) {
    if (getPlatform() !== "android") return;
    if (route === "bluetooth" && !bluetoothAvailable) return;
    const result = await setAudioRoute(route);
    if (result?.routed === false) return;
    setAudioRouteState(route);
    setIsSpeakerOn(route === "speaker");
  }

  async function endCall(overrideCallId = null) {
    try {
      cleanupRanRef.current = false;
      const callId =
        (typeof overrideCallId === "string" ? overrideCallId : null) ||
        outgoingCallId ||
        incomingCallId;
      const otherUserId =
        callPartnerIdRef.current ||
        outgoingCallTarget?.uid ||
        incomingCall?.from;

      setCallState("ending");
      logCallWithDuration("ended", otherUserId);

      if (callId) {
        await updateDoc(doc(db, "calls", callId), {
          status: "ended",
          endedAt: serverTimestamp(),
        });
        clearPendingAcceptEverywhere();
        cleanupCallById(callId, "remote");
      }
    } catch (err) {
      console.error("End call error:", err);
    }
  }

  const ensureCallKitCleared = async (reason = "failed") => {
    if (getPlatform() !== "ios") return;

    try {
      await clearPendingAccept();
    } catch (err) {
      console.warn("Failed to clear pending CallKit accept:", err);
    }

    const staleCallKitId =
      incomingCallKitIdRef.current ||
      outgoingCallKitIdRef.current ||
      null;
    if (!staleCallKitId) return;

    try {
      const staleCallId = incomingCallId || outgoingCallId || null;
      if (staleCallId && callKitEndedCallIdsRef.current.has(staleCallId)) {
        return;
      }
      if (staleCallId) {
        callKitEndedCallIdsRef.current.add(staleCallId);
      }
      await reportCallEnded(staleCallKitId, reason);
    } catch (err) {
      console.warn("Failed to clear stale CallKit call:", err);
    } finally {
      incomingCallKitIdRef.current = null;
      outgoingCallKitIdRef.current = null;
      callKitAudioActiveRef.current = false;
      pendingCallKitAudioResolveRef.current = null;
    }
  };

  const startRingback = () => {
    if (ringbackPlayingRef.current) return;
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;

    const context =
      ringbackContextRef.current || new AudioContext();
    ringbackContextRef.current = context;

    if (context.state === "suspended") {
      context.resume().catch(() => {});
    }

    const playBurst = () => {
      if (!ringbackPlayingRef.current) return;
      const gain = context.createGain();
      gain.gain.value = 0.08;
      gain.connect(context.destination);

      const oscA = context.createOscillator();
      const oscB = context.createOscillator();
      oscA.frequency.value = 440;
      oscB.frequency.value = 480;
      oscA.connect(gain);
      oscB.connect(gain);

      const now = context.currentTime;
      oscA.start(now);
      oscB.start(now);
      oscA.stop(now + 2.0);
      oscB.stop(now + 2.0);
    };

    ringbackPlayingRef.current = true;
    playBurst();
    ringbackTimerRef.current = setInterval(playBurst, 6000);
  };

  const stopRingback = () => {
    ringbackPlayingRef.current = false;
    if (ringbackTimerRef.current) {
      clearInterval(ringbackTimerRef.current);
      ringbackTimerRef.current = null;
    }
  };

  const markConnected = async (callId) => {
    if (!callId) return;
    if (connectedWrittenRef.current.has(callId)) return;
    connectedWrittenRef.current.add(callId);
    try {
      await updateDoc(doc(db, "calls", callId), {
        status: "connected",
        connectedAt: serverTimestamp(),
      });
    } catch (err) {
      console.warn("Failed to mark call connected:", err);
    }
  };

  async function startCall(targetUserId, targetMeta = {}, callerUser = null) {
    const baseUser =
      callerUser && callerUser.uid ? callerUser : effectiveUser;

    if (!baseUser?.uid) {
      console.warn("[CallEngine] startCall blocked: no auth user");
      return;
    }

    if (!targetUserId) {
      console.warn("[CallEngine] startCall blocked: missing targetUserId");
      return;
    }

    try {
      cleanupRanRef.current = false;
      await ensureCallKitCleared("failed");
      terminationReasonRef.current = null;
      setActivePeer({
        uid: targetUserId,
        name: targetMeta.name || "Unknown",
        rank: targetMeta.rank || "",
        agency: targetMeta.agency || "Unknown Agency",
        avatarUrl: targetMeta.avatarUrl || "",
      });
      setIsCalling(true);
      setOutgoingCallTarget({
        uid: targetUserId,
        name: targetMeta.name || "Unknown",
        rank: targetMeta.rank || "",
        agency: targetMeta.agency || "Unknown Agency",
        avatarUrl: targetMeta.avatarUrl || "",
      });

      callPartnerIdRef.current = targetUserId;
      setCallState("ringing_outgoing");

      // Create Firestore call document
      const callRef = await addDoc(collection(db, "calls"), {
        from: baseUser.uid,
        fromUserName:
          baseUser.firstInitialLastName ||
          baseUser.displayName ||
          "Unknown",
        fromRank: baseUser.rank || "",
        fromAgency: baseUser.agency || "Unknown Agency",
        fromAvatarUrl: baseUser.avatarUrl || "",
        to: targetUserId,
        status: "calling",
        createdAt: serverTimestamp(),
        callerCandidates: [],
        calleeCandidates: [],
      });

      setOutgoingCallId(callRef.id);
      console.log("Outgoing call created:", callRef.id);
      setCallProgressLabel("Calling...");
      setCallStatus("calling");
      callStatusRef.current = "calling";

      // 📞 Report outgoing call to CallKit (iOS only)
      // Required for proper Bluetooth routing, lock screen controls, and App Store approval
      if (getPlatform() === 'ios') {
        const calleeName = formatCallerName(
          targetMeta.name || "Unknown",
          targetMeta.agency || "Unknown Agency"
        );

        try {
          const callKitResult = await reportCallKitOutgoing(callRef.id, calleeName);
          if (callKitResult?.callKitId) {
            outgoingCallKitIdRef.current = callKitResult.callKitId;
          }
          console.log('✅ CallKit: Outgoing call reported');
        } catch (error) {
          console.error('❌ CallKit: Failed to report outgoing call:', error);
          // Continue with call even if CallKit fails
        }
      }

      console.log("Initializing WebRTC...");

      if (ringingTimeoutRef.current) {
        clearTimeout(ringingTimeoutRef.current);
      }
      ringingTimeoutRef.current = setTimeout(async () => {
        if (callStatusRef.current && callStatusRef.current !== "calling") return;
        console.log("Call failed – callee never rang");
        terminationReasonRef.current = "failed";
        try {
          await updateDoc(doc(db, "calls", callRef.id), {
            status: "failed",
          });
        } catch (err) {
          console.error("Failed to mark call failed:", err);
        }
        setCallProgressLabel("Call failed");
        setTimeout(() => cleanupOutgoingCall(), 1200);
      }, 15000);

      // iOS: wait for CallKit audio BEFORE any WebRTC initialization
      callKitAudioActiveRef.current = false;
      await awaitCallKitAudioActive();

      let pc = null;
      if (useNativeWebRTC) {
        nativeRemoteDescriptionReadyRef.current = false;
        await initializeNativeWebRTC(callRef.id, "caller");
      } else {
        pc = await createPeerConnection(callRef.id);

        console.log(
          "[BlueWire] DirectChat startCall -> requesting mic permission"
        );
        const hasPermission = await requestMicPermission();
        console.log(
          "[BlueWire] DirectChat startCall -> mic permission result:",
          hasPermission
        );
        if (!hasPermission) {
          console.error(
            "Microphone permission denied – cannot start call."
          );
          try {
            await updateDoc(doc(db, "calls", callRef.id), {
              status: "ended",
            });
          } catch (permissionErr) {
            console.error(
              "Failed to mark permission-denied call as ended:",
              permissionErr
            );
          }
          cleanupOutgoingCall();
          return;
        }

        console.log(
          "[BlueWire] DirectChat startCall -> requesting local audio stream"
        );
        const localStream = await getLocalAudioStream();
        console.log(
          "[BlueWire] DirectChat startCall -> acquired local audio stream"
        );

        localStream.getTracks().forEach((track) => {
          pc.addTrack(track, localStream);
        });
      }

      const offer = useNativeWebRTC
        ? await NativeWebRTC.createOffer()
        : await pc.createOffer();
      if (!useNativeWebRTC) {
        await pc.setLocalDescription(offer);
      }

      await updateDoc(doc(db, "calls", callRef.id), {
        offer,
      });

      console.log("Offer sent to Firestore.");

      if (!useNativeWebRTC) {
        pc.getTransceivers().forEach((t, i) => {
          console.log(`Outgoing Transceiver ${i} direction:`, t.currentDirection || t.direction);
        });
        setTimeout(async () => {
          if (pc.signalingState !== "closed") {
            try {
              const stats = await pc.getStats();
              let bytesSent = 0, bytesReceived = 0;
              stats.forEach(report => {
                if (report.type === 'outbound-rtp' && report.kind === 'audio') bytesSent = report.bytesSent;
                if (report.type === 'inbound-rtp' && report.kind === 'audio') bytesReceived = report.bytesReceived;
              });
              console.log(`Outgoing Audio Stats (3s) - Sent: ${bytesSent}, Received: ${bytesReceived}`);
            } catch (e) { console.warn("Failed to getStats:", e); }
          }
        }, 3000);
      }

      if (calleeCandidatesUnsubRef.current) {
        calleeCandidatesUnsubRef.current();
      }
      calleeCandidatesProcessedRef.current.clear();
      calleeCandidatesUnsubRef.current = useNativeWebRTC
        ? listenForCalleeCandidatesNative(callRef.id)
        : listenForCalleeCandidates(callRef.id, pc);

      // Guard against duplicate answer application
      let answerApplied = false;
      const terminalStatuses = new Set([
        "declined",
        "ended",
        "failed",
        "no_answer",
      ]);
      let terminalSeen = false;

      const unsub = onSnapshot(doc(db, "calls", callRef.id), (snap) => {
        const data = snap.data();
        if (!data) return;
        if (terminalCallIdsRef.current.has(callRef.id)) return;
        if (data.status) {
          const nextStatus = data.status;
          if (terminalSeen) return;
          if (terminalStatuses.has(callStatusRef.current)) {
            terminalSeen = true;
            return;
          }
          setCallStatus(nextStatus);
          callStatusRef.current = nextStatus;
          if (terminalStatuses.has(nextStatus)) {
            terminalSeen = true;
          }
        }

        if (data.answer) {
          if (useNativeWebRTC) {
            if (!nativeRemoteDescriptionReadyRef.current) {
              NativeWebRTC.setRemoteDescription(data.answer)
                .then(() => {
                  nativeRemoteDescriptionReadyRef.current = true;
                  return flushPendingCalleeCandidatesNative();
                })
                .catch((error) => {
                  console.error("Failed to apply remote answer:", error);
                });
            }
          } else if (!pc.currentRemoteDescription) {
            if (!answerApplied && pc.signalingState === "have-local-offer") {
              answerApplied = true;
              pc
                .setRemoteDescription(new RTCSessionDescription(data.answer))
                .then(() => {
                  flushPendingCalleeCandidates(pc);
                })
                .catch((error) => {
                  console.error("Failed to apply remote answer:", error);
                  answerApplied = false;
                });
            } else if (answerApplied) {
              console.log("Ignoring duplicate remote answer snapshot");
            } else {
              console.warn("Ignoring remote answer due to invalid signalingState:", pc.signalingState);
            }
          }
        }

        if (isDeclinedStatus(data)) {
          console.log("Callee declined call.");
          logCall("declined", callPartnerIdRef.current || targetUserId);
          stopRingback();
          setCallProgressLabel("Declined");
          if (ringingTimeoutRef.current) {
            clearTimeout(ringingTimeoutRef.current);
            ringingTimeoutRef.current = null;
          }
          if (noAnswerTimeoutRef.current) {
            clearTimeout(noAnswerTimeoutRef.current);
            noAnswerTimeoutRef.current = null;
          }
          cleanupCallById(callRef.id, "declined");
          return;
        }

        if (data.status === "accepted") {
          stopRingback();
          setCallProgressLabel("Connecting...");
          if (ringingTimeoutRef.current) {
            clearTimeout(ringingTimeoutRef.current);
            ringingTimeoutRef.current = null;
          }
          if (noAnswerTimeoutRef.current) {
            clearTimeout(noAnswerTimeoutRef.current);
            noAnswerTimeoutRef.current = null;
          }
          const callId = callRef.id;
          const alreadyHandled = acceptedHandledRef.current.has(callId);
          const isPreAcceptState =
            (callStateRef.current === "ringing_outgoing" ||
              callStateRef.current === "ringing_incoming") &&
            inCallRef.current === false;

          if (!alreadyHandled && isPreAcceptState) {
            acceptedHandledRef.current.add(callId);
            console.log("Call accepted!");

            setIsCalling(false);
            setInCall(true);
            setCallState("connected");
            if (callTimeoutRef.current) {
              clearTimeout(callTimeoutRef.current);
              callTimeoutRef.current = null;
            }
            callStartTimeRef.current = Date.now();
            logCall(
              "answered",
              callPartnerIdRef.current || targetUserId
            );
          }
        }

        if (data.status === "calling") {
          setCallProgressLabel("Calling...");
          stopRingback();
        }

        if (data.status === "ringing") {
          setCallProgressLabel("Ringing...");
          if (ringingTimeoutRef.current) {
            clearTimeout(ringingTimeoutRef.current);
            ringingTimeoutRef.current = null;
          }
          if (!noAnswerTimeoutRef.current) {
            noAnswerTimeoutRef.current = setTimeout(async () => {
              if (callStatusRef.current !== "ringing") return;
              console.log("Call no answer – ringing timed out");
              terminationReasonRef.current = "timeout";
              try {
                await updateDoc(doc(db, "calls", callRef.id), {
                  status: "no_answer",
                });
              } catch (err) {
                console.error("Failed to mark call no_answer:", err);
              }
              setCallProgressLabel("No answer");
              stopRingback();
              setTimeout(() => cleanupOutgoingCall(), 1200);
            }, 30000);
          }
          startRingback();
        }

        if (data.status === "connected") {
          stopRingback();
          setCallProgressLabel("");
          if (ringingTimeoutRef.current) {
            clearTimeout(ringingTimeoutRef.current);
            ringingTimeoutRef.current = null;
          }
          if (noAnswerTimeoutRef.current) {
            clearTimeout(noAnswerTimeoutRef.current);
            noAnswerTimeoutRef.current = null;
          }
        }

        if (data.status === "failed") {
          stopRingback();
          if (!terminationReasonRef.current) {
            terminationReasonRef.current = "failed";
          }
          setCallProgressLabel("Call failed");
          setTimeout(() => cleanupCallById(callRef.id, "failed"), 1200);
        }

        if (data.status === "no_answer") {
          stopRingback();
          if (!terminationReasonRef.current) {
            terminationReasonRef.current = "timeout";
          }
          setCallProgressLabel("No answer");
          setTimeout(() => cleanupCallById(callRef.id, "unanswered"), 1200);
        }

        if (data.status === "ended") {
          console.log("Remote user ended the call.");
          stopRingback();
          if (!terminationReasonRef.current) {
            terminationReasonRef.current = "remote_end";
          }
          logCallWithDuration(
            "ended",
            callPartnerIdRef.current || targetUserId
          );
          cleanupCallById(callRef.id, "remote");
        }
      }, (error) => {
        console.warn("[CallEngine] Active call listener error:", error);
        cleanupCallById(callRef.id, "remote").catch(() => {});
      });

      iceUnsubRef.current = unsub;
    } catch (err) {
      console.error("Error starting call:", err);
      cleanupOutgoingCall();
    }
  }

  async function cancelOutgoingCall() {
    if (!outgoingCallId) return;

    terminationReasonRef.current = "caller_cancelled";
    await endCall();
  }

  async function declineCall(overrideCallId = null) {
    const callId =
      (typeof overrideCallId === "string" ? overrideCallId : null) ||
      incomingCallId;
    if (!callId) return;

    try {
      logCall("declined", incomingCall?.from);

      terminationReasonRef.current = "declined";
      await updateDoc(doc(db, "calls", callId), {
        status: "declined",
        endedAt: serverTimestamp(),
      });
      clearPendingAcceptEverywhere();
    } catch (err) {
      console.error("Error declining call:", err);
    } finally {
      cleanupCallById(callId, "declined");
    }
  }

  async function performAccept(callId, callDoc) {
    if (!callId) {
      console.error("[BlueWire] Invalid incoming call payload");
      return;
    }
    if (callStateRef.current !== "ringing_incoming") return;
    if (acceptInProgressRef.current) {
      console.log("[CallEngine] acceptCall ignored; already in progress");
      return;
    }

    let resolvedCallDoc = callDoc;
    if (!resolvedCallDoc?.offer) {
      resolvedCallDoc = await waitForOffer(callId);
      if (resolvedCallDoc?.offer) {
        setIncomingCallData({ id: callId, ...resolvedCallDoc });
      }
    }

    if (!resolvedCallDoc?.offer) {
      console.error("[BlueWire] Invalid incoming call payload");
      return;
    }

    if (callStateRef.current !== "ringing_incoming") return;

    if (acceptedCallIdRef.current === callId) {
      console.log("[CallEngine] acceptCall ignored; already accepted", callId);
      return;
    }
    acceptedCallIdRef.current = callId;
    acceptInProgressRef.current = true;
    cleanupRanRef.current = false;
    pendingCallKitAcceptRef.current = null;
    cleanupCallKitHydration();
    clearPendingAcceptEverywhere();
    logColdStart("ColdStart: performAccept called").catch(() => {});
    const { from, fromUserName, fromAgency, offer } = resolvedCallDoc;

    console.log("[CallEngine] acceptCall invoked for", callId);

    // Stop ringing UI but keep call identity so activeCallId stays valid
    setIncomingCall(null);
    setCallState("connecting");
    callStateRef.current = "connecting";

    try {
      console.log("[BlueWire] Accepting call:", callId);

      await updateDoc(doc(db, "calls", callId), {
        status: "accepted",
        acceptedAt: serverTimestamp(),
      });

      let pc = null;
      if (useNativeWebRTC) {
        nativeRemoteDescriptionReadyRef.current = true;
        await initializeNativeWebRTC(callId, "callee");
        await logColdStart("ColdStart: peer created");
        await NativeWebRTC.setRemoteDescription(offer);
      } else {
        // iOS: wait for CallKit audio BEFORE any WebRTC initialization
        await awaitCallKitAudioActive();
        pc = await createPeerConnectionForAnswer(callId);
        await logColdStart("ColdStart: peer created");

        await pc.setRemoteDescription(
          new RTCSessionDescription(offer)
        );

        if (!(await requestMicPermission())) {
          console.error(
            "[BlueWire] Microphone permission denied - cannot accept call."
          );
          acceptInProgressRef.current = false;
          return;
        }

        console.log(
          "[BlueWire] handleAcceptCall -> requesting local audio stream"
        );
        const localStream = await getLocalAudioStream();
        console.log(
          "[BlueWire] handleAcceptCall -> acquired local audio stream"
        );

        localStream.getTracks().forEach((track) => {
          pc.addTrack(track, localStream);
        });
      }

      const answer = useNativeWebRTC
        ? await NativeWebRTC.createAnswer()
        : await pc.createAnswer();
      if (!useNativeWebRTC) {
        await pc.setLocalDescription(answer);
      }
      await logColdStart("ColdStart: answer sent");

      await updateDoc(doc(db, "calls", callId), {
        answer,
      });

      if (!useNativeWebRTC) {
        pc.getTransceivers().forEach((t, i) => {
          console.log(`Incoming Transceiver ${i} direction:`, t.currentDirection || t.direction);
        });
        setTimeout(async () => {
          if (pc.signalingState !== "closed") {
            try {
              const stats = await pc.getStats();
              let bytesSent = 0, bytesReceived = 0;
              stats.forEach(report => {
                if (report.type === 'outbound-rtp' && report.kind === 'audio') bytesSent = report.bytesSent;
                if (report.type === 'inbound-rtp' && report.kind === 'audio') bytesReceived = report.bytesReceived;
              });
              console.log(`Incoming Audio Stats (3s) - Sent: ${bytesSent}, Received: ${bytesReceived}`);
            } catch (e) { console.warn("Failed to getStats:", e); }
          }
        }, 3000);
      }

      if (callerCandidatesUnsubRef.current) {
        callerCandidatesUnsubRef.current();
      }
      callerCandidatesProcessedRef.current.clear();
      callerCandidatesUnsubRef.current = useNativeWebRTC
        ? listenForCallerCandidatesNative(callId)
        : listenForCallerCandidates(callId, pc);

      callPartnerIdRef.current = from;
      callStartTimeRef.current = Date.now();
      logCall("incoming", from);
      setActivePeer({
        uid: from,
        name: fromUserName || "Unknown",
        rank: resolvedCallDoc.fromRank || "",
        agency: fromAgency || "Unknown Agency",
        avatarUrl: resolvedCallDoc.fromAvatarUrl || "",
      });
      setInCall(true);
      setCallState("connected");
      clearColdStartTimers();
      clearPendingAcceptEverywhere();
      await logColdStart("ColdStart: reached in_call");
    } catch (err) {
      console.error("Error accepting call:", err);
    } finally {
      acceptInProgressRef.current = false;
    }
  }

  async function acceptCall() {
    if (!incomingCallId || !incomingCall) return;
    if (callStateRef.current !== "ringing_incoming") return;
    if (acceptedCallIdRef.current === incomingCallId) return;
    if (acceptInProgressRef.current) return;
    let callDoc = incomingCall;
    if (!callDoc?.offer) {
      const waited = await waitForOffer(incomingCallId, 10000);
      if (!waited || !waited.offer) {
        console.error("[CallEngine] Accept failed: offer not available");
        return;
      }
      callDoc = waited;
      setIncomingCallData(waited);
    }
    await performAccept(incomingCallId, callDoc);
  }

  function setIncomingCallData(callDoc) {
    if (!callDoc) {
      if (callState === "connected" || inCall) {
        console.log(
          "[CallEngine] setIncomingCallData(null) suppressed while connected/inCall"
        );
        return;
      }
      clearIncomingCallState();
      return;
    }

    if (terminalCallIdsRef.current.has(callDoc.id)) {
      console.log("[CallEngine] Ignoring terminal incoming call", callDoc.id);
      return;
    }

    if (
      inCallRef.current ||
      callStateRef.current === "connecting" ||
      callStateRef.current === "connected"
    ) {
      if (incomingCallIdRef.current && incomingCallIdRef.current !== callDoc.id) {
        console.log(
          "[CallEngine] Ignoring new incoming call for different id while busy",
          callDoc.id,
          "current incomingCallId=",
          incomingCallIdRef.current
        );
      } else {
        setIncomingCall(callDoc);
        setIncomingCallId(callDoc.id);
        incomingCallIdRef.current = callDoc.id;
      }
      return;
    }

    // If we already have a different incoming call, ignore this new one
    if (incomingCallId && incomingCallId !== callDoc.id) {
      console.log(
        "[CallEngine] Ignoring new incoming call for different id while busy",
        callDoc.id,
        "current incomingCallId=",
        incomingCallId
      );
      return;
    }

    setIncomingCall(callDoc);
    setIncomingCallId(callDoc.id);
    // Sync ref immediately — useEffect sync runs after render, too late
    // for CallKit listeners that may fire between now and the next render
    incomingCallIdRef.current = callDoc.id;
    terminationReasonRef.current = null;
    setCallState("ringing_incoming");
    callStateRef.current = "ringing_incoming";

    if (
      effectiveUser?.uid &&
      callDoc?.to === effectiveUser.uid &&
      callDoc?.status === "calling" &&
      !ringingWrittenRef.current.has(callDoc.id)
    ) {
      ringingWrittenRef.current.add(callDoc.id);
      updateDoc(doc(db, "calls", callDoc.id), {
        status: "ringing",
        ringingAt: serverTimestamp(),
      }).catch((err) => {
        console.warn("Failed to mark call ringing:", err);
      });
    }

    const pendingCallId = pendingCallKitAcceptRef.current;
    if (pendingCallId && pendingCallId === callDoc.id) {
      if (callKitAudioActiveRef.current) {
        console.log("📞 Audio ready; accepting pending CallKit call");
        pendingCallKitAcceptRef.current = null;
        acceptCall();
      } else {
        console.log("⏳ Waiting for CallKit audio before accepting");
      }
    }
  }

  useEffect(() => {
    if (callState !== "connected" || !callStartTimeRef.current) {
      setCallDuration(0);
      return;
    }

    const interval = setInterval(() => {
      setCallDuration(
        Math.floor((Date.now() - callStartTimeRef.current) / 1000)
      );
    }, 1000);

    return () => clearInterval(interval);
  }, [callState]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    if (callState === "connected" && !routingInitializedRef.current) {
      try {
        // Initialize route once per call to stabilize callee audio;
        // follow current isSpeakerOn state (earpiece vs speaker).
        setSpeakerRoute(isSpeakerOn);
        if (getPlatform() === "ios") {
          setCallKitSpeakerRoute(isSpeakerOn);
        }
      } catch (e) {
        console.warn("[CallEngine] initial route set failed", e);
      }
      routingInitializedRef.current = true;
    }

    if (callState === "idle") {
      routingInitializedRef.current = false;
      callKitAudioActiveRef.current = false;
      pendingCallKitAudioResolveRef.current = null;
    }
  }, [callState, isSpeakerOn]);

  useEffect(() => {
    if (!callState || callState === "idle") {
      return;
    }

    let armed = true;

    const unlock = () => {
      if (!armed) return;
      const audioEl = remoteAudioRef.current;
      if (audioEl) {
        audioEl.play().catch(() => { });
      }
      armed = false;
    };

    document.addEventListener("click", unlock, { once: true });
    document.addEventListener("touchstart", unlock, { once: true });

    return () => {
      armed = false;
      document.removeEventListener("click", unlock);
      document.removeEventListener("touchstart", unlock);
    };
  }, [callState]);

  useEffect(() => {
    console.log("[CallEngine][callee] state change", {
      callState,
      inCall,
      incomingCallId,
      outgoingCallId,
      hasPeer: !!activePeer,
    });
  }, [callState, inCall, incomingCallId, outgoingCallId, activePeer]);

  useEffect(() => {
    callStateRef.current = callState;
  }, [callState]);

  useEffect(() => {
    inCallRef.current = inCall;
  }, [inCall]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || getPlatform() !== "android") return;
    if (!inCall) return;

    let cancelled = false;
    const refresh = async () => {
      const available = await getBluetoothAvailable();
      if (!cancelled) {
        setBluetoothAvailable(available);
      }
    };
    refresh();

    return () => {
      cancelled = true;
    };
  }, [inCall]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || getPlatform() !== "android") return;

    const callActive = !!inCall;
    const proximityEnabled = callActive && audioRoute === "earpiece";
    syncProximityScreen(callActive, proximityEnabled);
  }, [inCall, audioRoute]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || getPlatform() !== "android") return;

    return () => {
      resetProximityScreen();
    };
  }, []);

  // Keep incomingCallIdRef in sync so CallKit listeners avoid stale closures
  useEffect(() => {
    incomingCallIdRef.current = incomingCallId;
  }, [incomingCallId]);

  useEffect(() => {
    incomingCallRef.current = incomingCall;
  }, [incomingCall]);

  // Keep outgoingCallIdRef in sync for persistent listeners
  useEffect(() => {
    outgoingCallIdRef.current = outgoingCallId;
  }, [outgoingCallId]);

  // Keep action refs current so persistent listeners always call latest functions
  actionsRef.current = { acceptCall, declineCall, endCall };

  // Engine-level watcher to clear stale incoming ringing state
  // when the caller times out or explicitly ends/declines the call.
  useEffect(() => {
    const shouldWatchIncoming =
      incomingCallId && callState === "ringing_incoming";

    if (!shouldWatchIncoming) {
      if (incomingStatusUnsubRef.current) {
        incomingStatusUnsubRef.current();
        incomingStatusUnsubRef.current = null;
      }
      incomingEndedLoggedRef.current = false;
      return;
    }

    const callRef = doc(db, "calls", incomingCallId);

    const unsub = onSnapshot(callRef, (snap) => {
      const data = snap.data();
      if (!data) return;

      if (
        (data.status === "ended" ||
          isDeclinedStatus(data) ||
          data.status === "failed" ||
          data.status === "no_answer") &&
        callState === "ringing_incoming" &&
        !inCall
      ) {
        if (!terminationReasonRef.current) {
          if (isDeclinedStatus(data)) {
            terminationReasonRef.current = "declined";
          } else if (data.status === "no_answer") {
            terminationReasonRef.current = "timeout";
          } else if (data.status === "ended") {
            terminationReasonRef.current = "remote_end";
          } else if (data.status === "failed") {
            terminationReasonRef.current = "failed";
          }
        }
        if (
          data.status === "no_answer" &&
          incomingCallId &&
          !missedCallNotifiedRef.current.has(incomingCallId)
        ) {
          const callerName =
            data.fromUserName ||
            incomingCallRef.current?.fromUserName ||
            "Unknown";
          const callerRank =
            data.fromRank || incomingCallRef.current?.fromRank || "";
          const callerAgency =
            data.fromAgency || incomingCallRef.current?.fromAgency || "";
          const callerLabel = `${callerRank ? `${callerRank} ` : ""}${callerName}${
            callerAgency ? ` (${callerAgency})` : ""
          }`;
          missedCallNotifiedRef.current.add(incomingCallId);
          postMissedCallNotification({
            callId: incomingCallId,
            callerLabel,
          }).catch((err) => {
            console.warn("Failed to post missed call notification:", err);
          });
        }
        if (!incomingEndedLoggedRef.current) {
          console.log(
            "[CallEngine] Incoming call timed out/ended before accept; clearing incoming state"
          );
          incomingEndedLoggedRef.current = true;
        }
        cleanupCallById(incomingCallId, "remote");
      }
    }, (error) => {
      console.warn("[CallEngine] Incoming status listener error:", error);
      cleanupCallById(incomingCallId, "remote").catch(() => {});
    });

    incomingStatusUnsubRef.current = unsub;

    return () => {
      if (incomingStatusUnsubRef.current) {
        incomingStatusUnsubRef.current();
        incomingStatusUnsubRef.current = null;
      }
    };
  }, [incomingCallId, callState, inCall]);

  const value = useMemo(
    () => ({
      state: {
        incomingCall,
        incomingCallId,
        outgoingCallId,
        outgoingCallTarget,
        isCalling,
        inCall,
        isMuted,
        isSpeakerOn,
        callState,
        callStatus,
        callProgressLabel,
        activeCallId: outgoingCallId || incomingCallId || null,
        callDuration,
        activePeer,
        appVisibility,
        audioRoute,
        bluetoothAvailable,
      },
      actions: {
        startCall,
        acceptCall,
        declineCall,
        endCall,
        toggleMute,
        toggleSpeaker,
        selectAudioRoute,
        cancelOutgoingCall,
        setIncomingCallData,
        // Visibility metadata only: must NEVER directly change call state,
        // routing, or notifications. Used by higher-level lifecycle host.
        setAppVisibility: (visibility) => {
          if (
            visibility === "foreground" ||
            visibility === "background" ||
            visibility === "restored"
          ) {
            setAppVisibility(visibility);
          }
        },
      },
      refs: {
        remoteAudioRef,
        calleeCandidatesUnsubRef,
        callTimeoutRef,
        terminationReasonRef,
      },
    }),
    [
      incomingCall,
      incomingCallId,
      outgoingCallId,
      outgoingCallTarget,
      isCalling,
      inCall,
      isMuted,
      isSpeakerOn,
      callState,
      callStatus,
      callProgressLabel,
      callDuration,
      activePeer,
      appVisibility,
      audioRoute,
      bluetoothAvailable,
    ]
  );

  if (!effectiveUser?.uid) {
    return (
      <CallSessionContext.Provider value={null}>
        {children}
      </CallSessionContext.Provider>
    );
  }

  return (
    <CallSessionContext.Provider value={value}>
      {children}
    </CallSessionContext.Provider>
  );
}
